import React, { useCallback, useEffect, useState } from 'react';
import FormInput from '../../../shared/components/ui/FormInput';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import Button from '../../../shared/components/ui/Button';
import { PreviewRateUnitID } from './PreviewRateUnitID';
import { PreviewRateAccommodationId } from './PreviewRateAccommodationId';
import {
  createPricing,
  getAllAccommodations,
  getAllAccommodationsByProject,
  getAllBlocks,
  getPlc,
  getPlcbyProjectId,
  getAllPricingByProjectId
} from '../../services/api/implementation/impl';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import Cookies from 'js-cookie';
import Select from 'react-select';
import mixpanel from 'mixpanel-browser';

export const CreateRateList = ({ projectName, createSuccess, pricingType, onClose }) => {
  const userId = Cookies.get('userId');
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors }
  } = useForm();
  const rawMeasurementUnit = Cookies.get('measurement_type');
  const measurementType =
    !rawMeasurementUnit || rawMeasurementUnit === 'null' || rawMeasurementUnit === 'undefined'
      ? 'sq/ft'
      : rawMeasurementUnit;
  const measurementTypeWithoutSlash = measurementType.replace('/', '');
  console.log({ measurementTypeWithoutSlash });

  const [selectedAccommodationType, setSelectedAccommodationType] = useState('GROUP');
  const [selectedAccommodations, setSelectedAccommodations] = useState([]);
  const [isOpenRateListById, setIsRateListOpenById] = useState(false);
  const [subCategory, setSubCategory] = useState('');
  const [onProccedbtn, setProccedbtn] = useState('');
  const { id: projectId } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [accommodationListOptions, setAccommodationListOptions] = useState([]);
  const [blocksListOptions, setBlocksListOptions] = useState([]);
  const [unitListOptions, setUnitListOptions] = useState([]);
  const [PLCListOptions, setPLCListOptions] = useState([]);
  const [selectedBlockId, setSelectedBlockId] = useState('');

  //Preview States
  const [formPayload, setFormPayload] = useState({});
  const [previewData, setPreviewData] = useState([]);

  // API Calls
  const { data: accommodations, refetch: refetchAccommodations } = useQuery({
    queryKey: ['accommodations', projectId, selectedAccommodationType],
    queryFn: () =>
      getAllAccommodationsByProject(projectId, `&accomodationType=${selectedAccommodationType}`),
    enabled: !!projectId && !!selectedAccommodationType
  });

  // Get all pricing data to check which accommodations already have pricing
  const { data: getAllPricingList } = useQuery({
    queryKey: ['getAllPricingList', projectId],
    queryFn: () => getAllPricingByProjectId(projectId),
    enabled: !!projectId
  });

  const { data: blocksDyna, refetch: refetchBlocks } = useQuery({
    queryKey: ['blocks', projectId],
    queryFn: () => getAllBlocks(projectId),
    enabled: !!projectId
  });

  const { data: PLCbyProjectIdList, refetch: refetchPLCProject } = useQuery({
    queryKey: ['PLCbyProjectIdList'],
    queryFn: () => getPlc(),
    enabled: !!projectId
  });

  // Track page view
  useEffect(() => {
    mixpanel.track('Create Rate List Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    if (accommodations && getAllPricingList) {
      const accommodationNameList = Object.values(accommodations).flat();
      if (accommodationNameList.length > 0) {
        // Create a set of accommodation IDs that already have pricing
        const accommodationsWithPricing = new Set(
          getAllPricingList
            .filter(
              (pricing) => pricing.accommodation?.accomodationType === selectedAccommodationType
            )
            .map((pricing) => pricing.accommodation_id)
        );

        // Filter out accommodations that already have pricing and match the selected type
        const availableAccommodations = accommodationNameList.filter(
          (item) =>
            !accommodationsWithPricing.has(item.id) &&
            item.accomodationType === selectedAccommodationType
        );

        setAccommodationListOptions(
          availableAccommodations.map((item) => ({
            value: item.id,
            label: item.name,
            type: item.accomodationType
          }))
        );
      } else {
        setAccommodationListOptions([]);
      }
    } else {
      setAccommodationListOptions([]);
    }
  }, [accommodations, getAllPricingList, selectedAccommodationType]);

  useEffect(() => {
    if (blocksDyna && blocksDyna.blocks) {
      setBlocksListOptions([
        { value: 'Select type', label: 'Select type' },
        ...blocksDyna.blocks.map((item) => ({ value: item.id, label: item.name }))
      ]);
    }
  }, [blocksDyna]);

  useEffect(() => {
    if (PLCbyProjectIdList) {
      const filterList = PLCbyProjectIdList.filter((item) => item.id === parseInt(selectedBlockId));
      const extractPLC = filterList.flatMap((item) => item.plcs);
      const PLCData = extractPLC?.map((plc) => ({ value: plc.id, label: plc.name }));
      setPLCListOptions([{ value: 'Select type', label: 'Select type' }, ...PLCData]);
    }
  }, [PLCbyProjectIdList, selectedBlockId]);

  useEffect(() => {
    if (blocksDyna && blocksDyna.blocks) {
      const filteredBlocks = blocksDyna.blocks.filter(
        (item) => item.id === parseInt(selectedBlockId)
      );
      const extractedUnits = filteredBlocks.flatMap((item) =>
        item.levels.flatMap((level) => level.units)
      );
      const unitsData = extractedUnits?.map((unit) => ({
        value: unit.id,
        label: unit.unitName
      }));
      setUnitListOptions([{ value: 'Select type', label: 'Select type' }, ...unitsData]);
    }
  }, [blocksDyna, selectedBlockId]);

  const handlePreviewAccmmodationIDonProccedbtn = () => {
    setProccedbtn('previewAccommodationById');
    setSubCategory('');
  };

  const handlePreviewUnitIDonProccedbtn = () => {
    setProccedbtn('previewUnitById');
    setSubCategory('');
  };

  const handleAccommodationTypeChange = (e) => {
    setSelectedAccommodationType(e.target.value);
    setSelectedAccommodations([]); // Reset selected accommodations when type changes
  };

  const handleAccommodationChange = (selected) => {
    setSelectedAccommodations(selected || []);
  };

  const onSubmit = (data) => {
    setPreviewData([]);

    // Create an array of payloads for all selected accommodations
    const payloads = selectedAccommodations.map((accommodation) => {
      const basePayload = {
        mode: subCategory === 'sqft' || subCategory === 'sqmt' ? 'sqft' : 'accommodation',
        accommodation_id: accommodation.value,
        project_id: parseInt(projectId)
      };

      if (subCategory === measurementTypeWithoutSlash) {
        basePayload.loading_factor = parseFloat(data.loadingFactor) || 0;

        if (selectedAccommodationType === 'INDEPENDENT') {
          basePayload.plote_price = parseInt(data.plote_price) || 0;
          basePayload.construction_price = parseInt(data.construction_price) || 0;
        } else {
          basePayload.price = parseInt(data.rate_per_sqft) || 0;
        }
      } else if (subCategory === 'accommodation') {
        if (selectedAccommodationType === 'INDEPENDENT') {
          basePayload.plote_price = parseInt(data.plote_price) || 0;
          basePayload.construction_price = parseInt(data.construction_price) || 0;
        } else {
          basePayload.price = parseInt(data.price) || 0;
        }
      }

      return basePayload;
    });

    // Create preview data for all accommodations
    const previewPayloads = selectedAccommodations.map((accommodation) => {
      const previewPayload = {
        project_name: projectName || '',
        accommodation_name: accommodation.label,
        mode: subCategory === 'sqft' || subCategory === 'sqmt' ? 'sqft' : 'accommodation'
      };

      if (subCategory === measurementTypeWithoutSlash) {
        previewPayload.loading_factor = data.loadingFactor;
      }

      if (selectedAccommodationType === 'INDEPENDENT') {
        previewPayload.plote_price = data.plote_price;
        previewPayload.construction_price = data.construction_price;
      } else {
        previewPayload.price =
          subCategory === measurementTypeWithoutSlash ? data.rate_per_sqft : data.price;
      }

      return previewPayload;
    });

    setFormPayload({ data: payloads });
    setPreviewData(previewPayloads);

    // Call the mutation with the formatted payload
    handleCreatePricing({ data: payloads });
  };

  const { mutate: handleCreatePricing } = useMutation({
    mutationFn: (payload) => createPricing(payload),
    onMutate: () => {
      setIsSubmitting(true);
    },
    onSuccess: (data) => {
      setIsSubmitting(false);
      toast.success('Pricing created successfully');
      setProccedbtn('');
      setFormPayload({});
      setPreviewData([]);
      createSuccess();
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error creating pricing', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error creating pricing');
    }
  });

  const handleSelectBlockChange = (e) => {
    setSelectedBlockId(e.target.value);
  };

  const handleCreatePricingClick = () => {
    handleCreatePricing(formPayload);
  };

  console.log(pricingType, 'pricingType');

  const [loadingFactor, setLoadingFactor] = useState('');

  const handleLoadingFactorChange = (e) => {
    let inputVal = e;

    // 1) Remove anything that's not a digit:
    inputVal = inputVal.replace(/\D/g, '');

    // 2) Convert to a number and cap at 100:
    const numericVal = parseInt(inputVal, 10);
    if (!isNaN(numericVal) && numericVal > 100) {
      inputVal = '100';
      toast.error('Loading factor cannot exceed 100');
    }

    // 3) Set our local state and also setValue in RHF so the form sees this updated value.
    setLoadingFactor(inputVal);
    setValue('loadingFactor', inputVal);
  };

  return (
    <div>
      {onProccedbtn === 'previewAccommodationById' && (
        <PreviewRateAccommodationId
          previewData={previewData}
          isSubmitting={isSubmitting}
          handlePreviewProceed={handleCreatePricingClick}
          onEdit={() => {
            setIsRateListOpenById(true);
            setProccedbtn('');
            setSubCategory('accommodation');
          }}
        />
      )}
      {onProccedbtn === 'previewUnitById' && (
        <PreviewRateUnitID
          previewData={previewData}
          isSubmitting={isSubmitting}
          handlePreviewProceed={handleCreatePricingClick}
          onEdit={() => {
            setIsRateListOpenById(true);
            setProccedbtn('');
            setSubCategory(measurementTypeWithoutSlash);
          }}
        />
      )}
      {isOpenRateListById ? (
        <form onSubmit={handleSubmit(onSubmit)}>
          {(subCategory === measurementTypeWithoutSlash || subCategory === 'accommodation') && (
            <>
              <div>
                <div className="flex flex-col justify-between items-start gap-4">
                  <span className="text-lg border-b-[1px] border-stone-200 w-full pb-2">
                    {subCategory === measurementTypeWithoutSlash
                      ? `${measurementType} Based Rate Details`
                      : 'Accommodation Based Rate Details'}
                  </span>
                </div>
                <div className="flex justify-between mt-4">
                  {/* Accommodation Type Dropdown */}
                  <div className="w-[49%]">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Accommodation Type
                    </label>
                    <select
                      className="w-full p-2 border border-gray-300 rounded-md"
                      value={selectedAccommodationType}
                      onChange={handleAccommodationTypeChange}>
                      <option value="GROUP">GROUP</option>
                      <option value="INDEPENDENT">INDEPENDENT</option>
                    </select>
                  </div>

                  {/* Multi-select Accommodation Dropdown */}
                  <div className="w-[49%]">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Accommodation Name
                    </label>
                    <Select
                      isMulti
                      options={accommodationListOptions}
                      value={selectedAccommodations}
                      onChange={handleAccommodationChange}
                      className="basic-multi-select"
                      classNamePrefix="select"
                    />
                  </div>
                </div>

                {subCategory === measurementTypeWithoutSlash && (
                  <div className="flex justify-between mt-4">
                    <FormInput
                      width={49}
                      label={'Loading Factor (%)'}
                      register_key={'loadingFactor'}
                      register={register}
                      type={'text'}
                      onType={handleLoadingFactorChange}
                      defaultValuePre={loadingFactor}
                      errors={errors.loadingFactor}
                    />
                  </div>
                )}

                <div className="flex w-full justify-between mt-4">
                  {selectedAccommodationType === 'INDEPENDENT' ? (
                    <div className="flex justify-between mt-4 w-full">
                      <FormInput
                        width={49}
                        label={'Plot Price'}
                        register_key={'plote_price'}
                        register={register}
                        isRequired={true}
                        type={'text'}
                      />
                      <FormInput
                        width={49}
                        label={'Construction Price'}
                        register_key={'construction_price'}
                        register={register}
                        type={'text'}
                      />
                    </div>
                  ) : (
                    <FormInput
                      width={49}
                      label={
                        subCategory === measurementTypeWithoutSlash
                          ? `Rate per ${measurementType}`
                          : 'Price'
                      }
                      register_key={
                        subCategory === measurementTypeWithoutSlash ? 'rate_per_sqft' : 'price'
                      }
                      isRequired={true}
                      register={register}
                      type={'text'}
                    />
                  )}
                </div>

                <div className="w-full flex justify-end items-center space-x-4 mt-8">
                  <Button type="reset" displayType="secondary" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={selectedAccommodations.length === 0}>
                    Proceed
                  </Button>
                </div>
              </div>
            </>
          )}
        </form>
      ) : (
        <div className="flex gap-10 justify-center h-[15rem] items-center">
          {pricingType !== 'sqft' && (
            <div
              className="p-5 shadow-md font-semibold flex flex-col items-center cursor-pointer"
              onClick={() => {
                setIsRateListOpenById(true);
                setSubCategory('accommodation');
              }}>
              <h3>Accommodation Based Rate List</h3>
              <p className="text-sm text-[#BCC1CA]">Accommodation Based Rate List</p>
            </div>
          )}
          {(pricingType === 'sqft' || pricingType === 'all') && (
            <div
              className="p-5 shadow-md cursor-pointer font-semibold flex flex-col items-center"
              onClick={() => {
                setIsRateListOpenById(true);
                setSubCategory(measurementTypeWithoutSlash);
              }}>
              <h3>{measurementTypeWithoutSlash} Based Rate List</h3>
              <p className="text-sm text-[#BCC1CA]">{measurementTypeWithoutSlash} Rate List</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
